import { useState } from 'react';
import { styled } from '@mui/material/styles';
import { Box, Stack, AppBar, Toolbar, IconButton, Menu, MenuItem, Badge, Typography, ListItemIcon } from '@mui/material';
import useOffSetTop from '../../hooks/useOffSetTop';
import cssStyles from '../../utils/cssStyles';
import { HEADER, NAVBAR } from '../../config';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import SettingsOutlined from '@mui/icons-material/SettingsOutlined';
import useSettings from '../../hooks/useSettings';
import { Login, ThemeToggle } from '@microsoft/mgt-react';
import { Notifications } from '@mui/icons-material';
import { useNotifications } from '../../contexts/NotificationsContext';
import { PATH_EMAIL_RECIPIENTS, PATH_TENANTS, PATH_PAGE } from '../../routes/paths';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { HelpOutlined } from '@mui/icons-material';


type RootStyleProps = {
  isCollapse: boolean;
  isOffset: boolean;
  verticalLayout: boolean;
};

const RootStyle = styled(AppBar, {
  shouldForwardProp: (prop) =>
    prop !== 'isCollapse' && prop !== 'isOffset' && prop !== 'verticalLayout'
})<RootStyleProps>(({ isCollapse, isOffset, verticalLayout, theme }) => ({
  ...cssStyles(theme).bgBlur(),
  boxShadow: 'none',
  height: HEADER.MOBILE_HEIGHT,
  zIndex: theme.zIndex.appBar + 1,
  transition: theme.transitions.create(['width', 'height'], {
    duration: theme.transitions.duration.shorter
  }),
  [theme.breakpoints.up('lg')]: {
    height: HEADER.DASHBOARD_DESKTOP_HEIGHT,
    width: `calc(100% - ${NAVBAR.DASHBOARD_WIDTH + 1}px)`,
    ...(isCollapse && {
      width: `calc(100% - ${NAVBAR.DASHBOARD_COLLAPSE_WIDTH}px)`
    }),
    ...(isOffset && {
      height: HEADER.DASHBOARD_DESKTOP_OFFSET_HEIGHT
    }),
    ...(verticalLayout && {
      width: '100%',
      height: HEADER.DASHBOARD_DESKTOP_OFFSET_HEIGHT,
      backgroundColor: theme.palette.background.default
    })
  }
}));

type Props = {
  onOpenSidebar: VoidFunction;
  isCollapse?: boolean;
  verticalLayout?: boolean;
};

export default function DashboardHeader({
  onOpenSidebar,
  isCollapse = false,
  verticalLayout = false
}: Props) {
  const isOffset =
    useOffSetTop(HEADER.DASHBOARD_DESKTOP_HEIGHT) && !verticalLayout;

  const { onToggleMode, themeMode } = useSettings();
  const { notifications, clearNotifications } = useNotifications();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [anchorSetting, setAnchorSetting] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const openSetting = Boolean(anchorSetting);
  const navigate = useNavigate();
  const handleMenuNotification = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseNotifications = () => {
    setAnchorEl(null);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorSetting(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorSetting(null);
  };

  const handleCreateTenant = () => {
    handleMenuClose();
    navigate(PATH_TENANTS.general.create);
  };

  const handleListOfTenants = () => {
    handleMenuClose();
    navigate(PATH_TENANTS.general.view);
  };

  const handleListOfEmailRecipients = () => {
    handleMenuClose();
    navigate(PATH_EMAIL_RECIPIENTS.general.view);
  };

  const handleHelpClick = () => {
    navigate(PATH_PAGE.help);
  };

  return (
    <RootStyle isCollapse={false} isOffset={isOffset} verticalLayout={false}>
      <Toolbar sx={{ minHeight: '100% !important', marginLeft: 'auto', pl: { lg: 5 } }}>
        <Box sx={{ flexGrow: 1 }} />

        <Stack direction="row" alignItems="center" spacing={1.5}>
          <IconButton onClick={handleHelpClick}>
            <HelpOutlined />
          </IconButton>
          <IconButton onClick={onToggleMode}>
            {themeMode === 'light' ? <DarkModeOutlinedIcon /> : <LightModeOutlinedIcon />}
          </IconButton>

          <IconButton onClick={handleMenuNotification}>
            <Badge badgeContent={notifications.length} sx={{
              '& .MuiBadge-badge': {
                backgroundColor: '#b91c1c',
                color: 'white',
                fontSize: '0.75rem',
                fontWeight: 'bold',
                padding: '4px 6px'
              }
            }}>
              <Notifications />
            </Badge>
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleCloseNotifications}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem>
              <Typography variant="h6">Notifications</Typography>
            </MenuItem>
            {notifications.length > 0 ? (
              notifications.map((notif: any) => (
                <MenuItem key={notif.id}>
                  <Typography variant="body2">{notif.message}</Typography>
                </MenuItem>
              ))
            ) : (
              <MenuItem>
                <Typography variant="body2" color="textSecondary">
                  No notifications
                </Typography>
              </MenuItem>
            )}
            {notifications.length > 0 && <MenuItem onClick={clearNotifications}>
              <Typography variant="body2" className='text-red-700'>
                Clear All
              </Typography>
            </MenuItem>}
          </Menu>

          <IconButton onClick={handleMenuOpen}>
            <SettingsOutlined />
          </IconButton>
          <Menu
            anchorEl={anchorSetting}
            open={openSetting}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem onClick={handleCreateTenant}>
              <ListItemIcon>
                <AddIcon fontSize="small" />
              </ListItemIcon>
              Create Tenant
            </MenuItem>

            <MenuItem onClick={handleListOfTenants}>
              <ListItemIcon>
                <ListAltIcon fontSize="small" />
              </ListItemIcon>
              List of Tenants
            </MenuItem>

            <MenuItem onClick={handleListOfEmailRecipients}>
              <ListItemIcon>
                <MailOutlineIcon fontSize="small" />
              </ListItemIcon>
              Email Recipients
            </MenuItem>
          </Menu>

          <Login />
          <ThemeToggle className="hidden" darkModeActive={themeMode === 'dark'} />
        </Stack>
      </Toolbar>
    </RootStyle>
  );
}
